import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Link2, Send, Power, Smartphone, Monitor, X, Search, UserPlus } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/access`;
const when = (v) => (v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

/**
 * Access links (docs/OL_ACCESS_LINKS.md) — every person in the org, their personal link and
 * signed-in devices. Send / copy a link (a new one replaces the old), turn a person off or on,
 * remove a device. No passwords.
 */
export default function PeopleAccessPanel({ orgId }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);
  const [add, setAdd] = useState(null);
  const [sel, setSel] = useState(() => new Set());

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/${orgId}/people`, { withCredentials: true }); setRows(data.people || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load people'); setRows([]); }
  }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const link = async (p, send) => {
    if (!send && !window.confirm(`Make a new link for ${p.first_name}? Their old link stops working.`)) return;
    setBusy(`${p.id}:link`);
    try {
      const { data } = await axios.post(`${API}/${orgId}/people/${p.id}/link`, { send }, { withCredentials: true });
      if (send) toast.success(data.emailed ? `Link emailed to ${p.email}` : 'Link made — email could not be sent');
      else { await navigator.clipboard?.writeText(data.url); toast.success('New link copied — send it to them'); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not make a link'); }
    finally { setBusy(''); }
  };
  const toggle = async (p) => {
    const off = !p.disabled;
    if (off && !window.confirm(`Turn off ${p.first_name} ${p.last_name}? Their link and every signed-in device stop.`)) return;
    setBusy(`${p.id}:off`);
    try { await axios.post(`${API}/${orgId}/people/${p.id}/disable`, { disabled: off }, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not change'); }
    finally { setBusy(''); }
  };
  const removeDevice = async (p, d) => {
    try { await axios.delete(`${API}/${orgId}/people/${p.id}/devices/${d.id}`, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };
  const create = async () => {
    try {
      const { data } = await axios.post(`${API}/${orgId}/people`, add, { withCredentials: true });
      await navigator.clipboard?.writeText(data.url);
      toast.success('Added — their link is copied'); setAdd(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
  };

  const bulk = async (body, label) => {
    if (!window.confirm(`${label}? Each person gets a new personal link by email (an older link stops working).`)) return;
    setBusy('bulk');
    try {
      const { data } = await axios.post(`${API}/${orgId}/people/send-links`, body, { withCredentials: true });
      setSel(new Set());
      let j = { status: 'running', sent: 0, failed: 0, total: data.total };
      while (j.status === 'running') {
        toast.loading(`Emailing links… ${j.sent + j.failed} of ${j.total}`, { id: 'bulk-links' });
        await new Promise(r => setTimeout(r, 2000));
        j = (await axios.get(`${API}/${orgId}/people/send-links/${data.job_id}`, { withCredentials: true })).data;
      }
      toast.success(`Links emailed: ${j.sent}${j.failed ? ` · not sent: ${j.failed} (their old link still works)` : ''}`, { id: 'bulk-links' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send links'); }
    finally { setBusy(''); }
  };
  const pick = (id) => setSel(s0 => { const n = new Set(s0); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  if (!rows) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const shown = rows.filter(p => !q || `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  const canPick = shown.filter(p => !p.disabled && p.email);
  const inp = 'h-8 text-sm rounded border px-2 bg-white dark:bg-slate-900 dark:border-slate-600';
  return (
    <div className="space-y-3" data-testid="people-access">
      <p className="text-xs text-slate-500">Every club member for the new sign-in: their personal link, phone app and computers — no passwords. A new link replaces the old one. Turning someone off stops their link and every device they're signed in on. During the switch-over, people marked <b>password</b> can still use the old website sign-in (managed under Website logins).</p>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" /><input className={`${inp} pl-7 w-64`} placeholder="Search name or email" value={q} onChange={e => setQ(e.target.value)} data-testid="people-search" /></div>
        <span className="text-xs text-slate-500">{shown.length} of {rows.length}</span>
        {sel.size > 0 && <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" disabled={!!busy} onClick={() => bulk({ user_ids: [...sel] }, `Email links to ${sel.size} selected`)} data-testid="people-send-selected"><Send className="h-3.5 w-3.5 mr-1" />Email links to {sel.size} selected</Button>}
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={() => bulk({ without_link: true }, `Email links to everyone without one (${rows.filter(p => !p.disabled && p.email && !p.link).length})`)} data-testid="people-send-all">{busy === 'bulk' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}Email links to everyone without one</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs ml-auto" onClick={() => setAdd({ first_name: '', last_name: '', email: '', phone: '' })} data-testid="people-add"><UserPlus className="h-3.5 w-3.5 mr-1" />Add person</Button>
      </div>
      {add && (
        <div className="flex flex-wrap gap-2 items-center rounded border p-2 dark:border-slate-700" data-testid="people-add-form">
          {['first_name', 'last_name', 'email', 'phone'].map(k => <input key={k} className={`${inp} w-40`} placeholder={k.replace('_', ' ')} value={add[k]} onChange={e => setAdd(a => ({ ...a, [k]: e.target.value }))} />)}
          <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={create}>Add + copy link</Button>
          <button onClick={() => setAdd(null)} aria-label="Cancel"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="rounded border overflow-x-auto dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500"><tr className="text-left border-b dark:border-slate-700">
            <th className="p-2 w-6"><input type="checkbox" aria-label="Select all shown" checked={canPick.length > 0 && canPick.every(p => sel.has(p.id))} onChange={e => setSel(e.target.checked ? new Set(canPick.map(p => p.id)) : new Set())} /></th>
            <th className="p-2">Person</th><th className="p-2">Access</th><th className="p-2">Link</th><th className="p-2">Devices</th><th className="p-2">Last seen</th><th className="p-2"></th></tr></thead>
          <tbody>
            {shown.map(p => (
              <React.Fragment key={p.id}>
                <tr className={`border-b dark:border-slate-700 ${p.disabled ? 'opacity-50' : ''}`} data-testid={`person-${p.id}`}>
                  <td className="p-2">{!p.disabled && p.email && <input type="checkbox" aria-label={`Select ${p.first_name}`} checked={sel.has(p.id)} onChange={() => pick(p.id)} />}</td>
                  <td className="p-2"><div className="font-medium">{p.first_name} {p.last_name}</div><div className="text-xs text-slate-500">{p.email}{p.password_login && <span className="ml-1 rounded bg-slate-100 dark:bg-slate-800 px-1 text-[10px]" title="Also has an old website email + password sign-in">password</span>}</div></td>
                  <td className="p-2 capitalize">{p.disabled ? 'off' : p.access}</td>
                  <td className="p-2 text-xs">{p.link ? (p.link.last_used_at ? `used ${when(p.link.last_used_at)}` : 'sent, not used yet') : <span className="text-slate-400">none</span>}</td>
                  <td className="p-2 text-xs">{p.devices.length ? <button className="underline" onClick={() => setOpen(open === p.id ? null : p.id)}>{p.devices.length} signed in</button> : '—'}</td>
                  <td className="p-2 text-xs">{when(p.last_seen_at)}</td>
                  <td className="p-2 whitespace-nowrap text-right">
                    {!p.disabled && p.email && <button title="Email them a new link" className="p-1 text-slate-500 hover:text-amber-500" disabled={!!busy} onClick={() => link(p, true)} data-testid={`person-send-${p.id}`}>{busy === `${p.id}:link` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>}
                    {!p.disabled && <button title="Copy a new link" className="p-1 text-slate-500 hover:text-amber-500" disabled={!!busy} onClick={() => link(p, false)} data-testid={`person-copy-${p.id}`}><Link2 className="h-4 w-4" /></button>}
                    <button title={p.disabled ? 'Turn back on' : 'Turn off'} className={`p-1 ${p.disabled ? 'text-emerald-600' : 'text-slate-500 hover:text-red-500'}`} disabled={!!busy} onClick={() => toggle(p)} data-testid={`person-toggle-${p.id}`}><Power className="h-4 w-4" /></button>
                  </td>
                </tr>
                {open === p.id && p.devices.map(d => (
                  <tr key={d.id} className="text-xs bg-slate-50 dark:bg-slate-800/40">
                    <td className="p-2 pl-6" colSpan={4}>{d.kind === 'app' ? <Smartphone className="h-3.5 w-3.5 inline mr-1" /> : <Monitor className="h-3.5 w-3.5 inline mr-1" />}{d.name}</td>
                    <td className="p-2" colSpan={2}>last seen {when(d.last_seen_at)}</td>
                    <td className="p-2 text-right"><button className="text-red-500 underline" onClick={() => removeDevice(p, d)}>Remove</button></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
